import Phaser from 'phaser';
import { PALETTE, TEXT_COLOUR, FONT, px } from '../theme';

/**
 * Ein Texteingabefeld.
 *
 * Phaser bringt keines mit, und ein echtes DOM-`<input>` ueber den Canvas zu
 * legen brauecht eigene Positionierung bei jeder Groessenaenderung. Fuer ein
 * einzelnes Namensfeld genuegt es, die Tastatur der Szene abzuhoeren und den
 * Text selbst zu zeichnen — samt blinkender Schreibmarke.
 */

export interface TextInputOptions {
  readonly width: number;
  readonly value: string;
  readonly maxLength?: number;
  readonly fontSize?: number;
  readonly onChange: (value: string) => void;
}

export interface TextInput {
  readonly container: Phaser.GameObjects.Container;
  readonly focus: () => void;
  readonly blur: () => void;
  readonly destroy: () => void;
}

/** Zeichen, die in einem Namen vorkommen duerfen. */
const ALLOWED = /^[\p{L}\p{M} '\-.]$/u;

export function createTextInput(
  scene: Phaser.Scene,
  x: number,
  y: number,
  options: TextInputOptions,
): TextInput {
  const height = 34;
  const maxLength = options.maxLength ?? 28;
  const fontSize = options.fontSize ?? 17;

  let value = options.value;
  let focused = false;

  const frame = scene.add.rectangle(0, 0, options.width, height, PALETTE.panel, 0.6).setOrigin(0, 0);
  frame.setStrokeStyle(1, PALETTE.panelEdge);

  const label = scene.add.text(10, height / 2, value, {
    fontFamily: FONT.display,
    fontSize: px(fontSize),
    color: PALETTE.ink,
  });
  label.setOrigin(0, 0.5);

  const caret = scene.add.rectangle(0, height / 2, 1.5, fontSize + 4, PALETTE.coast).setOrigin(0, 0.5);
  caret.setVisible(false);

  const container = scene.add.container(x, y, [frame, label, caret]);
  container.setSize(options.width, height);
  container.setInteractive(
    new Phaser.Geom.Rectangle(options.width / 2, height / 2, options.width, height),
    Phaser.Geom.Rectangle.Contains,
  );
  container.input!.cursor = 'text';

  const positionCaret = (): void => {
    caret.x = 10 + label.width + 2;
  };

  const render = (): void => {
    label.setText(value);
    positionCaret();
  };

  const blink = scene.time.addEvent({
    delay: 500,
    loop: true,
    callback: () => caret.setVisible(focused && !caret.visible),
  });

  const setFocused = (next: boolean): void => {
    focused = next;
    frame.setStrokeStyle(1, next ? PALETTE.coast : PALETTE.panelEdge);
    caret.setVisible(next);
    label.setColor(next ? TEXT_COLOUR.coast : PALETTE.ink);
  };

  const onKey = (event: KeyboardEvent): void => {
    if (!focused) return;

    if (event.key === 'Backspace') {
      value = value.slice(0, -1);
    } else if (event.key === 'Enter' || event.key === 'Escape') {
      setFocused(false);
      return;
    } else if (ALLOWED.test(event.key) && value.length < maxLength) {
      value += event.key;
    } else {
      return;
    }

    // Der Browser darf die Rücktaste nicht als „zurück" auslegen.
    event.preventDefault();
    render();
    options.onChange(value);
  };

  scene.input.keyboard?.on('keydown', onKey);
  container.on('pointerdown', () => setFocused(true));

  // Ein Klick daneben beendet die Eingabe.
  scene.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
    const bounds = new Phaser.Geom.Rectangle(x, y, options.width, height);
    if (!bounds.contains(pointer.x, pointer.y)) setFocused(false);
  });

  render();

  return {
    container,
    focus: () => setFocused(true),
    blur: () => setFocused(false),
    destroy: () => {
      blink.destroy();
      scene.input.keyboard?.off('keydown', onKey);
      container.destroy();
    },
  };
}
